import { Provider, useAtomValue, useStore } from "jotai";
import { Platform } from "react-native";
import { RelayEnvironmentProvider, useRelayEnvironment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";

import { BiometricSignInButton } from "../../../BiometricAuthentication/BiometricSignInButton";
import { sessionCurrentClubAtom } from "../../../Session";
import { VerifiedAuthenticationModal } from "./VerifiedAuthenticationModal/VerifiedAuthenticationModal";
import { VerifiedAuthenticationModalStepState } from "./VerifiedAuthenticationModal/types";

type Props = {
  isOpen: boolean;
  onClose?: () => void;
  onComplete: (clubSlug?: string) => void;
  clubSlug?: string;
  preferredBiometricClubSlug?: string | null;
  hideCloseButton?: boolean;
  initialStep?: VerifiedAuthenticationModalStepState;
};

export const AuthenticationModal = ({
  isOpen,
  onClose,
  onComplete,
  clubSlug,
  preferredBiometricClubSlug,
  hideCloseButton,
  initialStep,
}: Props) => {
  const relayEnvironment = useRelayEnvironment();
  // The form has its own store; biometric login must update the app session.
  const sessionStore = useStore();
  const sessionClub = useAtomValue(sessionCurrentClubAtom);
  const trackEvent = useTrackEvent();
  return isOpen ? (
    <StandardModal
      isOpen={isOpen}
      disableContentScroll
      hideCloseButton={true}
      onClose={() => {
        trackEvent("AuthModal.Closed", {});
        onClose?.();
      }}
      content={
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <VerifiedAuthenticationModal
            onClose={onClose}
            onComplete={onComplete}
            clubSlug={clubSlug}
            hideCloseButton={hideCloseButton}
            initialStep={initialStep}
            sessionClub={sessionClub}
            alternativeSignIn={
              Platform.OS !== "web" ? (
                <Provider store={sessionStore}>
                  <BiometricSignInButton
                    onComplete={onComplete}
                    onCancel={() => undefined}
                    preferredClubSlug={
                      clubSlug ??
                      preferredBiometricClubSlug ??
                      sessionClub?.slug
                    }
                  />
                </Provider>
              ) : undefined
            }
          />
        </RelayEnvironmentProvider>
      }
    />
  ) : null;
};
